// Lifts the splash from public/index.html once the page can play its intro in one go, so
// fonts and images don't pop in halfway through the animations. Until then html.is-loading
// keeps every intro animation paused (main.css).
const MIN_VISIBLE_MS = 900;
const MAX_WAIT_MS = 4000;
const LIFT_FALLBACK_MS = 1500;
// Share of the splash exit that plays before the page intro starts
const INTRO_AT = 0.75;

// Requested explicitly: layout may not have asked for every face yet
const FONTS = [
  '800 1em Unbounded',
  '700 1em Unbounded',
  '400 1em "Open Sans"',
  '600 1em "Open Sans"',
  '600 1em Belanosima',
  '400 1em "Martian Mono"',
];

const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

const windowLoaded = () =>
  new Promise((resolve) => {
    if (document.readyState === 'complete') resolve();
    else window.addEventListener('load', () => resolve(), { once: true });
  });

const fontsLoaded = () => {
  if (!document.fonts?.load) return Promise.resolve();
  return Promise.all(FONTS.map((font) => document.fonts.load(font).catch(() => undefined))).then(
    () => document.fonts.ready
  );
};

// The first screen's images, decoded so they appear with the animation rather than after it
const imagesDecoded = () =>
  Promise.all(
    Array.from(document.querySelectorAll('.site-header img, .hero img')).map((image) =>
      image.decode ? image.decode().catch(() => undefined) : undefined
    )
  );

const lift = () => {
  const root = document.documentElement;
  const splash = document.getElementById('splash');
  if (!splash) {
    root.classList.remove('is-loading');
    return;
  }

  // The page intro stays paused until the splash exit has played this far
  let introStarted = false;
  const startIntro = () => {
    if (introStarted) return;
    introStarted = true;
    root.classList.remove('is-loading');
  };

  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    startIntro();
    root.classList.remove('is-lifting');
    splash.remove();
  };

  root.classList.add('is-lifting');
  const exitMs = parseFloat(getComputedStyle(splash).animationDuration) * 1000 || 0;
  setTimeout(startIntro, exitMs * INTRO_AT);
  splash.addEventListener('animationend', (event) => event.target === splash && finish());
  setTimeout(finish, LIFT_FALLBACK_MS);
};

export const liftSplash = () => {
  if (!document.documentElement.classList.contains('is-loading')) return;
  const shownAt = window.__splashShownAt ?? performance.now();
  const elapsed = () => performance.now() - shownAt;

  const ready = windowLoaded()
    .then(nextFrame)
    .then(() => Promise.all([fontsLoaded(), imagesDecoded()]));
  const capped = new Promise((resolve) => setTimeout(resolve, Math.max(0, MAX_WAIT_MS - elapsed())));

  Promise.race([ready, capped]).then(() => {
    setTimeout(() => requestAnimationFrame(lift), Math.max(0, MIN_VISIBLE_MS - elapsed()));
  });
};
